import { createStreamedReply } from "@workspace/api-client-react";
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send, Sparkles, Loader2, Maximize2, Minimize2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { classifyGateError } from "@/lib/premiumGate";
import { useLanguage } from "@/hooks/useLanguage";
import { Link, useLocation } from "wouter";
import { AiMarkdown } from "@/components/AiMarkdown";
import PrivacyBadge from "@/components/PrivacyBadge";

// Focused, full-attention flows where the floating launcher would overlap the
// flow's own primary action buttons on small screens (e.g. the onboarding
// wizard's "Reveal my snapshot" CTA or the Relationship Coach composer). Hide
// the assistant entirely there.
const HIDDEN_ROUTE_PREFIXES = [
  "/landing",
  "/pricing",
  "/team",
  "/team-admin",
  "/onboarding",
  "/relationship-coach",
];

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  streaming?: boolean;
  action?: "upgrade" | "signin";
}

export default function AstroAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: "assistant",
      content: "Hello! I'm Astro, your cosmic guide. Ask me anything about zodiac signs, compatibility, birth charts, or how astrology connects to your wellness journey. ✨"
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const streamRequestRef = useRef<AbortController | null>(null);
  useEffect(() => () => streamRequestRef.current?.abort(), []);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  const handleClose = () => {
    setIsOpen(false);
    setIsExpanded(false);
  };

  const [pathname] = useLocation();
  const hidden = HIDDEN_ROUTE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    const newMessages: ChatMessage[] = [...messages, { role: "user", content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);
    const streamedReply = createStreamedReply<ChatMessage>(setMessages, (content) => ({
      role: "assistant", content,
    }));
    const streamController = new AbortController();
    streamRequestRef.current = streamController;

    try {
      const history = newMessages
        .filter((m) => m !== newMessages[0])
        .map((m) => ({ role: m.role, content: m.content }));

      const res = await apiRequest("POST", "/api/astro-assistant", {
        message: userMessage,
        history,
        language,
      }, { signal: streamController.signal, onDraft: streamedReply.update });
      const data = await res.json();
      streamedReply.finish(data.response);
    } catch (err) {
      streamedReply.discard();
      if (streamController.signal.aborted) return;
      const kind = classifyGateError(err);
      const errorMessage: ChatMessage =
        kind === "upgrade"
          ? {
              role: "assistant",
              content: "This feature is part of a premium plan. Upgrade to keep the cosmic conversation going. ✨",
              action: "upgrade",
            }
          : kind === "signin"
            ? {
                role: "assistant",
                content: "Please sign in to chat with Astro.",
                action: "signin",
              }
            : {
                role: "assistant",
                content: "The cosmic connection wavered. Please try again in a moment.",
              };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      if (!streamController.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  if (hidden) return null;

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-[5.5rem] md:bottom-24 right-4 md:right-6 z-50 w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-violet-600 via-fuchsia-600 to-pink-600 text-white shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center group animate-glow"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5rem)' }}
          aria-label="Open Astro Assistant"
        >
          <MessageCircle className="h-6 w-6 group-hover:hidden" />
          <Sparkles className="h-6 w-6 hidden group-hover:block" />
        </button>
      )}

      {isOpen && (
        <div
          className={`fixed z-50 flex flex-col overflow-hidden shadow-2xl border border-violet-500/20 transition-all duration-300 ${
            isExpanded
              ? "inset-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[min(800px,calc(100vw-2rem))] sm:h-[min(700px,calc(100vh-2rem))] rounded-none sm:rounded-2xl"
              : "bottom-[5.5rem] md:bottom-24 right-4 md:right-6 w-[360px] max-w-[calc(100vw-2rem)] h-[450px] md:h-[500px] max-h-[calc(100vh-6rem)] rounded-2xl"
          }`}
          style={isExpanded ? undefined : { bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5rem)' }}
        >
          <div className="bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Astro Assistant</h3>
                <p className="text-white/70 text-xs">Your cosmic guide</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsExpanded((prev) => !prev)}
                className="text-white/80 hover:text-white transition-colors p-1 rounded"
                aria-label={isExpanded ? "Minimize chat" : "Maximize chat"}
                data-testid="astro-assistant-maximize-toggle"
              >
                {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
              <button
                onClick={handleClose}
                className="text-white/80 hover:text-white transition-colors p-1 rounded"
                aria-label="Close chat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-background">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-br-sm"
                      : "bg-muted text-foreground rounded-bl-sm"
                  }`}
                >
                  {msg.role === "user" ? msg.content : <AiMarkdown content={msg.content} />}
                  {msg.action === "upgrade" && (
                    <Link
                      href="/pricing"
                      onClick={() => setIsOpen(false)}
                      className="mt-2 inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 px-3 py-1.5 text-xs font-semibold text-white hover:from-violet-500 hover:to-fuchsia-500 transition-colors"
                      data-testid="link-upgrade-astro-assistant"
                    >
                      <Sparkles className="h-3 w-3" />
                      Upgrade
                    </Link>
                  )}
                  {msg.action === "signin" && (
                    <Link
                      href="/login"
                      onClick={() => setIsOpen(false)}
                      className="mt-2 inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 px-3 py-1.5 text-xs font-semibold text-white hover:from-violet-500 hover:to-fuchsia-500 transition-colors"
                      data-testid="link-signin-astro-assistant"
                    >
                      Sign in
                    </Link>
                  )}
                </div>
              </div>
            ))}
            {isLoading && !messages.some((message) => message.streaming) && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-2.5 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-violet-600 dark:text-violet-400" />
                  <span className="text-sm">Reading the stars...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-3 border-t border-border bg-background flex-shrink-0">
            <PrivacyBadge compact className="mb-2" />
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendMessage();
              }}
              className="flex gap-2"
            >
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("chat.placeholder", "Ask about zodiac, compatibility...")}
                className="flex-1 rounded-xl text-sm"
                maxLength={500}
                disabled={isLoading}
              />
              <Button
                type="submit"
                size="icon"
                disabled={!input.trim() || isLoading}
                className="rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white flex-shrink-0"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
